import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ChargingType } from '../../../assets/icons/charging-type.svg';
import { ReactComponent as CheckBoxActive } from '../../../assets/icons/check-box-active-icon.svg';
import { ReactComponent as CheckBox } from '../../../assets/icons/check-box-icon.svg';
import { ReactComponent as Clock } from '../../../assets/icons/clock-icon.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as Search } from '../../../assets/icons/search-icon.svg';
import { ReactComponent as DefaultStationImage } from '../../../assets/icons/station-icon-view.svg';
import stationImage from '../../../assets/images/profile.jpg';
import { appCurrentBranding } from '../../../config/branding';
import { GuestChargingDetails } from '../../../interface/partner-interfaces/guest-charging.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slices/NotificationSlice';
import { fetchGuestCharging } from '../../../redux/slices/Partners/GuestChargingSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomDateRanges from '../../../shared-components/custom-date-range/CustomDateRanges';
import CustomMultiDatePicker from '../../../shared-components/custom-date-range/CustomMultiDatePicker';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomHoursLimit from '../../../shared-components/custom-hours-limit/CustomHoursLimit';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import CustomMultiTimeSession from '../../../shared-components/custom-time-input/CustomMultiTimeSession';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import ConvertTimeToDate from '../../../shared-functions/ConvertDateToTime';
import ConvertToUTC from '../../../shared-functions/ConvertToUTC';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import FormatUtcString from '../../../shared-functions/FormatUtcString';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import {
  dateValidation,
  emailValidation,
  phoneNumberValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import deviceApi from '../../../utils/api/partner/device-partner';
import guestChargingApi from '../../../utils/api/partner/guest-charging';
import stationApi from '../../../utils/api/partner/station';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Sorting data
 * @param {any} a - The a
 * @param {any} a.name - The name prop
 * @param {any} b - The b
 * @param {any} b.name - The name prop
 * @returns {any} The result
 */
const handleSort = (a: { name: string }, b: { name: string }) => {
  const isNumberA = /^\d+$/.test(a.name);
  const isNumberB = /^\d+$/.test(b.name);

  // If both are numbers → compare numerically
  if (isNumberA && isNumberB) {
    return Number(a.name) - Number(b.name);
  }

  // If A is number but B is not → A comes first
  if (isNumberA && !isNumberB) return -1;

  // If B is number but A is not → B comes first
  if (!isNumberA && isNumberB) return 1;

  // Both are strings → use natural sort (handles numeric inside text)
  return a.name.localeCompare(b.name, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

/**
 * Add new station component
 */
interface AddGuestChargingProperties {
  closeSidePanel?: () => void;
}

/**
 * Add new station component
 * @param {any} param0 - The param0
 * @param {any} param0.closeSidePanel - The closeSidePanel prop
 */
function AddGuestCharging({ closeSidePanel = undefined }: AddGuestChargingProperties) {
  const { t, i18n } = useTranslation('guestCharging');
  const dispatch = useAppDispatch();
  const { page, limit } = useAppSelector((state) => state.guestCharging);
  const [guestDetails, setGuestDetails] = useState<GuestChargingDetails>({
    name: '',
    type: 'phone',
    email: '',
    phoneNumber: '',
    userType: 'single',
    countryCode: appCurrentBranding === 'energic' ? '966' : '1',
    profile: '',
    time: [],
    hour: '',
    minutes: '',
    fromDate: '',
    toDate: '',
    selectedDates: [],
    stationList: [],
    deviceList: [],
    bulkImport: [],
    rfidNo: '',
  });

  const [multiGuestUser, setMultiGuestUser] = useState<
    { name: string; phoneNumber: string; countryCode: string }[]
  >([]);

  const [multiGuest, setMultiGuest] = useState<{
    name: string;
    phoneNumber: string;
    countryCode: string;
  }>({
    name: '',
    phoneNumber: '',
    countryCode: appCurrentBranding === 'energic' ? '966' : '1',
  });

  const [search, setSearch] = useState<string | number>('');
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });
  const [station, setStationList] = useState<any[]>([]);
  // devices per station
  const [devicesByStation, setDevicesByStation] = useState<Record<string, any[]>>({});
  const visibleDevices = guestDetails.stationList.flatMap(
    (stationId) => devicesByStation[stationId] || [],
  );

  const connectorTypes = [
    {
      label: t('singleUser'),
      value: 'single',
    },
    {
      label: t('multiUser'),
      value: 'multi',
    },
  ];
  const profileList = [
    {
      label: t('profile1'),
      value: 'profile1',
    },
    {
      label: t('profile2'),
      value: 'profile2',
    },
  ];

  /**
   * Get the all station list function
   */
  const fetchAllStationsList = () => {
    stationApi
      .fetchAllStationDetails()
      .then((response) => {
        setStationList(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Get the device details use station id function
   * @param {any} stationId - The stationId
   */
  const fetchAllStationList = (stationId: string) => {
    deviceApi
      .fetchAllDeviceDetailsList(stationId)
      .then((response) => {
        setDevicesByStation((previous) => ({
          ...previous,
          [stationId]: response.data,
        }));
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle input value change
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleStationDetailChange = (
    name: string,
    changeValue: string | number | string[] | File[],
  ) => {
    // ✅ If fromDate changes → reset toDate and selectedDates
    if (name === 'fromDate') {
      setGuestDetails((previous) => ({
        ...previous,
        fromDate: changeValue as string,
        toDate: '', // clear toDate
        selectedDates: [], // clear multi date picker
      }));
      return;
    }

    // Optional: If toDate becomes earlier than fromDate, clear selectedDates
    if (name === 'toDate') {
      setGuestDetails((previous) => ({
        ...previous,
        toDate: changeValue as string,
        selectedDates: [],
      }));
      return;
    }

    // Existing logic
    if (guestDetails.type === 'phone') {
      setGuestDetails({
        ...guestDetails,
        email: '',
        [name]: changeValue,
      });
    } else {
      setGuestDetails({
        ...guestDetails,
        phoneNumber: '',
        [name]: changeValue,
      });
    }
  };
  /**
   * Handle input value change
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleGuestDetailsChange = (name: string, changeValue: string | number) => {
    setMultiGuest({
      ...multiGuest,
      [name]: changeValue,
    });
  };
  /**
   * Handle input value change
   * @param {any} event - The mouse event.
   * @param {any} name - The field name.
   * @param {any} stationId - The station ID to add or remove.
   */
  const addStations = (event: React.MouseEvent, name: string, stationId: string) => {
    event.stopPropagation();
    let updatedStations: string[] = [];

    if (guestDetails.stationList.includes(stationId)) {
      // ❌ removing station
      updatedStations = guestDetails.stationList.filter((id) => id !== stationId);

      // remove its devices also
      setDevicesByStation((previous) => {
        const copy = { ...previous };
        delete copy[stationId];
        return copy;
      });

      // remove selected devices of that station
      setGuestDetails((previous) => ({
        ...previous,
        stationList: updatedStations,
        deviceList: previous.deviceList.filter(
          (deviceSn) => !devicesByStation[stationId]?.some((d) => d.chargingPointSn === deviceSn),
        ),
      }));
    } else {
      // ✅ adding station
      updatedStations = [...guestDetails.stationList, stationId];

      setGuestDetails((previous) => ({
        ...previous,
        stationList: updatedStations,
      }));
    }
  };
  /**
   * Handle input value change
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const addDevices = (name: string, changeValue: string) => {
    let output = [];
    if (guestDetails.deviceList.includes(changeValue)) {
      output = guestDetails.deviceList.filter((stationId) => stationId !== changeValue);
    } else {
      output = [...guestDetails.deviceList, changeValue];
    }
    setGuestDetails({
      ...guestDetails,
      [name]: output,
    });
  };
  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setGuestDetails({
      name: '',
      type: 'phone',
      email: '',
      phoneNumber: '',
      userType: '',
      countryCode: '',
      profile: '',
      time: [],
      hour: '0',
      minutes: '0',
      fromDate: '',
      toDate: '',
      selectedDates: [],
      stationList: [],
      deviceList: [],
      bulkImport: [],
      rfidNo: '',
    });
    setModalBoxStatus('close');

    if (closeSidePanel) {
      closeSidePanel();
    }
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title={t('cancelTitle')}
          description={t('cancelDescription')}
          cancelButtonLabel={t('cancel')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('ok')}
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * add the multi guest user
   */
  const addMultiGuestUser = () => {
    setMultiGuestUser([...multiGuestUser, multiGuest]);
    setMultiGuest({
      name: '',
      phoneNumber: '',
      countryCode: appCurrentBranding === 'energic' ? '966' : '1',
    });
  };
  /**
   * Delete the multi guest user
   * @param {any} index - The index
   */
  const deleteGuestUser = (index: number) => {
    setMultiGuestUser(multiGuestUser.filter((_, index_) => index_ !== index));
  };

  /**
   * Add station API call
   */
  const handleSubmitStationDetails = () => {
    setFormStatus({
      showFormErrors: true,
      buttonLoader: false,
    });

    const isNameValid = stringValidation(1, 100, guestDetails.name);
    const isContactValid =
      guestDetails.type === 'phone'
        ? phoneNumberValidation(guestDetails.phoneNumber) && guestDetails.countryCode
        : emailValidation(guestDetails.email);
    const isDateValid =
      dateValidation(guestDetails.fromDate) &&
      dateValidation(guestDetails.toDate) &&
      guestDetails.fromDate <= guestDetails.toDate;
    const isHoursValid = guestDetails.hour || guestDetails.minutes;
    const isDeviceSelected = guestDetails.deviceList.length > 0;

    if (isNameValid && isContactValid && isDateValid && isHoursValid && isDeviceSelected) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: true,
      });

      const timeSlots =
        guestDetails.time.length > 0
          ? guestDetails.time.map((value) => {
              const time = value.split(' - ');
              const startTime = ConvertTimeToDate(time[0]);
              let toTime: any = ConvertTimeToDate(time[1]);

              if (startTime > toTime) {
                const date = new Date(toTime);
                date.setUTCDate(date.getUTCDate() + 1);
                toTime = date.toISOString();
              }
              return {
                startTime,
                endTime: toTime,
              };
            })
          : [
              {
                startTime: ConvertTimeToDate('12:00 AM'),
                endTime: ConvertTimeToDate('11:59 PM'),
              },
            ];

      // Filter stationList to only include stations that have at least one selected device
      const filteredStationList = guestDetails.stationList.filter((stationId) =>
        devicesByStation[stationId]?.some((device) =>
          guestDetails.deviceList.includes(device.chargingPointSn),
        ),
      );

      const request: any = {
        name: guestDetails.name,
        durationLimitInMins:
          (parseInt(guestDetails.hour, 10) * 60 || 0) + (parseInt(guestDetails.minutes, 10) || 0),
        chargingStationIds: filteredStationList,
        chargingPoints: guestDetails.deviceList,
        onSelectiveDates: guestDetails.selectedDates.map((selectedDate) =>
          ConvertToUTC('00:00', selectedDate),
        ),
        fromDate: ConvertToUTC('00:00', guestDetails.fromDate),
        toDate: ConvertToUTC('00:00', guestDetails.toDate),
        timeSlots,
        rfidNo: guestDetails.rfidNo,
      };

      if (guestDetails.type === 'phone') {
        request.phoneNumber = guestDetails.phoneNumber;
        request.countryCode = guestDetails.countryCode;
      } else {
        request.email = guestDetails.email;
      }

      guestChargingApi
        .addGuestCharging(request)
        .then(() => {
          dispatch(fetchGuestCharging({ page, limit }));
          setModalBoxStatus('open');
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });

          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('guestAddedSuccess')}
                description={t('guestAddedSuccess')}
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          setModalBoxStatus('close');
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    }
  };

  useEffect(() => {
    fetchAllStationsList();
  }, []);

  useEffect(() => {
    // Fetch devices for all selected stations on mount or change
    if (guestDetails.stationList.length > 0) {
      guestDetails.stationList.forEach((stationId) => {
        if (!devicesByStation[stationId]) {
          fetchAllStationList(stationId);
        }
      });
    }
  }, [guestDetails.stationList.length]);

  return (
    <div className="add-Guest-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="add-Guest-header">
        <PageTitle title={t('addGuest')} />
      </div>
      <div className="add-Guest-container">
        <div className="add-Guest-from-wrap">
          <div className="form-field">
            {false && (
              <SelectFiled
                inputLabel=""
                name="userType"
                placeholder={t('selectUserType')}
                options={connectorTypes}
                selectedValue={guestDetails.userType}
                valueRequired
                onChangeValue={handleStationDetailChange}
              />
            )}
            {guestDetails.userType === 'multi' && (
              <SelectFiled
                inputLabel=""
                name="profile"
                placeholder={t('selectProfile')}
                options={profileList}
                selectedValue={guestDetails.profile}
                valueRequired
                onChangeValue={handleStationDetailChange}
              />
            )}
            {guestDetails.userType === 'multi' && (
              <CustomFileInput
                inputLabel=""
                acceptedFileType="application/pdf"
                placeholder={t('chooseFileBulkImport')}
                name="bulkImport"
                selectedFiles={guestDetails.bulkImport}
                onChangeValue={handleStationDetailChange}
              />
            )}
          </div>
          <div className="add-guest-input-form">
            {guestDetails.userType === 'multi' && (
              <div className="form-row-2">
                <div className="column-field">
                  <CustomInput
                    inputLabel={t('guestName')}
                    type="text"
                    placeholder={t('enterName')}
                    name="name"
                    value={multiGuest.name}
                    onChangeValue={handleGuestDetailsChange}
                  />
                  <CustomPhoneNumber
                    inputLabel={t('phoneNumber')}
                    placeholder={t('enterPhoneNumber')}
                    name="phoneNumber"
                    value={multiGuest.phoneNumber}
                    onChangeValue={handleGuestDetailsChange}
                    countryCode={multiGuest.countryCode}
                  />
                  <div className="form-buttons-wrap">
                    <MyButton
                      label={t('add')}
                      buttonType="submit"
                      onClickFunc={addMultiGuestUser}
                    />
                  </div>
                </div>

                <div className="column-field">
                  <table className="table">
                    <tr className="header">
                      <td className="sno">{t('sno')}</td>
                      <td>{t('guestName')}</td>
                      <td>{t('phoneNumber')}</td>
                      <td className="action">{t('action')}</td>
                    </tr>
                    {multiGuestUser.map((guestUser, index) => (
                      <tr>
                        <td className="sno">{index + 1}</td>
                        <td>{guestUser.name}</td>
                        <td>
                          +{guestUser.countryCode} {guestUser.phoneNumber}
                        </td>
                        <td className="action">
                          <div
                            className={`icon ${!permissionCheck('mobilityGuestUserDelete') ? 'disabled-action' : ''}`}
                            onClick={() => {
                              if (!permissionCheck('mobilityGuestUserDelete')) {
                                return;
                              }
                              deleteGuestUser(index);
                            }}
                            role="presentation">
                            <DeleteIcon />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </table>
                </div>
              </div>
            )}
            {guestDetails.userType !== 'multi' && (
              <div className="form-row-2">
                <div className="guest-name-input">
                  <CustomInput
                    inputLabel={t('guestName')}
                    type="text"
                    placeholder={t('enterName')}
                    name="name"
                    value={guestDetails.name}
                    onChangeValue={handleStationDetailChange}
                    valueRequired
                    error={
                      !stringValidation(1, 100, guestDetails.name) && formStatus.showFormErrors
                    }
                    errorMessage={t('guestNameRequired')}
                  />
                </div>
                <div className="input-toggle">
                  <div className="radio-Button">
                    <RadioButton
                      name="type"
                      value={guestDetails.type}
                      list={[
                        {
                          label: t('phoneNumber'),
                          value: 'phone',
                        },
                        {
                          label: t('email'),
                          value: 'email',
                        },
                      ]}
                      onChangeValue={handleStationDetailChange}
                    />
                  </div>
                  {guestDetails.type === 'phone' ? (
                    <CustomPhoneNumber
                      placeholder={t('enterPhoneNumber')}
                      name="phoneNumber"
                      value={guestDetails.phoneNumber}
                      onChangeValue={handleStationDetailChange}
                      countryCode={guestDetails.countryCode}
                      error={
                        !phoneNumberValidation(guestDetails.phoneNumber) &&
                        formStatus.showFormErrors
                      }
                      errorMessage={t('invalidPhoneNumber')}
                    />
                  ) : (
                    <CustomInput
                      type="text"
                      placeholder={t('enterEmail')}
                      name="email"
                      value={guestDetails.email}
                      onChangeValue={handleStationDetailChange}
                      error={!emailValidation(guestDetails.email) && formStatus.showFormErrors}
                      errorMessage={t('invalidEmail')}
                    />
                  )}
                </div>
              </div>
            )}
            <CustomInput
              type="text"
              placeholder={t('enterRFID')}
              name="rfidNo"
              inputLabel={t('rfidNumber')}
              value={guestDetails.rfidNo}
              onChangeValue={handleStationDetailChange}
            />
            <CustomDateRanges
              name1="fromDate"
              name2="toDate"
              inputLabel={t('date')}
              value1={guestDetails.fromDate}
              value2={guestDetails.toDate}
              onChangeValue={handleStationDetailChange}
              valueRequired
              disablePastDates
              error={
                !(
                  guestDetails.fromDate &&
                  guestDetails.toDate &&
                  dateValidation(guestDetails.fromDate) &&
                  dateValidation(guestDetails.toDate) &&
                  guestDetails.fromDate <= guestDetails.toDate
                ) && formStatus.showFormErrors
              }
              errorMessage={t('invalidDateRange')}
            />
            <CustomMultiDatePicker
              inputLabel={t('selectParticularDate')}
              placeholder=""
              name="selectedDates"
              value={guestDetails.selectedDates}
              onChangeValue={handleStationDetailChange}
              validFromDate={guestDetails.fromDate}
              validToDate={guestDetails.toDate}
            />
            <CustomMultiTimeSession
              inputLabel={t('time')}
              placeholder=""
              name="time"
              value={guestDetails.time}
              onChangeValue={handleStationDetailChange}
            />
            <CustomHoursLimit
              inputLabel={t('hoursLimit')}
              hourValue={guestDetails.hour}
              minutesValue={guestDetails.minutes}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={!guestDetails.hour && !guestDetails.minutes && formStatus.showFormErrors}
              errorMessage={t('hoursLimitRequired')}
            />
          </div>
          <div className="add-Guest-from">
            <div className="station-list">
              <div className="station-list-header">
                <CustomInput
                  svgIcon={<Search />}
                  inputLabel=""
                  type="text"
                  placeholder={t('searchStation')}
                  name="search"
                  value={search}
                  onChangeValue={(name, value) => setSearch(value)}
                />
              </div>
              <div className="station-list-body">
                <div className="station-list-contents">
                  {station
                    .filter((stationDetails) =>
                      stationDetails.name.toLowerCase().includes(String(search).toLowerCase()),
                    )
                    .sort((a, b) => handleSort(a, b))
                    .map((stationDetails) => (
                      <div className="station-item">
                        <div
                          className="check-box"
                          onClick={(event) => addStations(event, 'stationList', stationDetails._id)}
                          role="presentation">
                          {guestDetails.stationList.includes(stationDetails._id) ? (
                            <CheckBoxActive />
                          ) : (
                            <CheckBox />
                          )}
                        </div>
                        <div className="station-image">
                          <img
                            src={stationDetails?.medias?.[0]?.signedUrl || stationImage}
                            alt={t('stationImage')}
                          />
                        </div>
                        <div className="station-details">
                          <div className="station-name">{stationDetails.name}</div>
                          <div className="station-time">
                            {stationDetails.isAvailable ? (
                              <div className="tag-success-label">{t('active')}</div>
                            ) : (
                              <div className="tag-danger-label">{t('inactive')}</div>
                            )}
                            <div className="time">
                              <Clock /> {FormatUtcString(ConvertUTCtoHHMM(stationDetails.opensAt))}{' '}
                              - {FormatUtcString(ConvertUTCtoHHMM(stationDetails.closesAt))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              {guestDetails.deviceList.length === 0 && formStatus.showFormErrors && (
                <div className="error-message">{t('selectDevice')}</div>
              )}
            </div>
            <div className="device-list-card">
              <div className="card">
                <div className="card-contents">
                  <div className="device-list">
                    {visibleDevices.map((deviceDetails) => (
                      <div className="device-item">
                        <div
                          className="check-box"
                          onClick={() => addDevices('deviceList', deviceDetails.chargingPointSn)}
                          role="presentation">
                          {guestDetails.deviceList.includes(deviceDetails.chargingPointSn) ? (
                            <CheckBoxActive />
                          ) : (
                            <CheckBox />
                          )}
                        </div>
                        <div className="device-details">
                          <div className="device-details-contents-1">
                            <div className="device-icon">
                              <div className="icon">
                                <DefaultStationImage />
                              </div>
                            </div>
                            <div className="device-name">
                              <div className="device-title">
                                {deviceDetails.chargingPointName} - {deviceDetails.chargingPointSn}
                              </div>
                              <div className="device-title">{deviceDetails.connectorType}</div>
                            </div>
                            <div className="device-status">
                              {deviceDetails.isAvailable ? (
                                <div className="tag-success-label">{t('active')}</div>
                              ) : (
                                <div className="tag-danger-label">{t('inactive')}</div>
                              )}
                            </div>
                          </div>
                          <div className="device-details-contents-2">
                            <div className="connectors-list">
                              {deviceDetails.connectors.map((connector: any) => (
                                <div className="connector-item tag-default-label">
                                  <ChargingType /> {t('connectorLabel')} {connector?.connectorId}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="add-guest-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label={t('submit')}
              buttonType="submit"
              onClickFunc={handleSubmitStationDetails}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

AddGuestCharging.defaultProps = {
  closeSidePanel: undefined,
};

export default AddGuestCharging;
